import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "../../components/DailyBudgetInvoiceItemsTable/aggridCustom.css";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { CellClickedEvent, ColDef } from "ag-grid-community";
import { Alert, Box, Button, IconButton, Snackbar, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import useUpdateAuthToken from "../../hooks/useUpdateAuthToken";
import {
  useGetEducationItemsQuery,
  useDeleteEducationItemMutation,
} from "../../services/education/education";
import { EducationItem } from "../../types/types";
import { PageLayout } from "../../components/PageLayout/PageLayout";
import { PageContentBox } from "../shared/styledComponents";
import { EDUCATION_ITEM_LIST_TEXTS, SNACKBAR_AUTO_HIDE_MS } from "../shared/constants";
import { GREEN } from "../../styles/mui-theme";
import DeleteEducationItemDialog from "./DeleteEducationItemDialog";

const TABLE_HEIGHT = "700px";
const T = EDUCATION_ITEM_LIST_TEXTS;

const EducationItemListPage: React.FC = () => {
  useUpdateAuthToken();
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact<EducationItem> | null>(null);

  const { data: items, isFetching } = useGetEducationItemsQuery();
  const [deleteEducationItem, { isLoading: isDeleting }] = useDeleteEducationItemMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState<EducationItem | null>(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [searchText, setSearchText] = useState("");

  const rowData = useMemo(
    () => (isFetching || !items ? undefined : items.map((i) => ({ ...i }))),
    [isFetching, items],
  );

  const columnDefs: ColDef<EducationItem>[] = useMemo(
    () => [
      {
        headerName: T.colSlug,
        field: "slug",
        filter: true,
        flex: 1,
        cellStyle: { cursor: "pointer", color: GREEN, textDecoration: "underline" },
      },
      { headerName: T.colId, field: "id", filter: true, width: 100 },
      { headerName: T.colName, field: "name", filter: true, flex: 1 },
      { headerName: T.colCategory, field: "category", filter: true, width: 140 },
      { headerName: T.colStatus, field: "status", filter: true, width: 120 },
      { headerName: T.colCapacity, field: "max_capacity", width: 100 },
      {
        headerName: T.colActions,
        width: 100,
        pinned: "right",
        sortable: false,
        filter: false,
        cellRenderer: (params: { data: EducationItem }) => (
          <IconButton
            size="small"
            color="error"
            data-action="delete"
            onClick={(e) => {
              e.stopPropagation();
              setSelected(params.data);
              setDialogOpen(true);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [],
  );

  const onCellClicked = useCallback(
    (event: CellClickedEvent<EducationItem>) => {
      if (event.colDef.field === "slug" && event.data?.slug) {
        navigate(`/education-item-maintenance/${event.data.slug}`);
      }
    },
    [navigate],
  );

  const handleConfirmDelete = async () => {
    if (!selected?.slug) return;
    try {
      await deleteEducationItem(selected.slug).unwrap();
      setSuccessMsg(T.deleteSuccess);
    } catch {
      setErrorMsg(T.deleteError);
    } finally {
      setDialogOpen(false);
      setSelected(null);
    }
  };

  return (
    <PageLayout fullWidth showSearch={false}>
      <PageContentBox>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            {T.pageTitle}
          </Typography>
          <Box display="flex" gap={1} alignItems="center">
            <TextField
              size="small"
              placeholder="Buscar..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5 }} /> }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/education-item-maintenance/new")}
            >
              {T.createButton}
            </Button>
          </Box>
        </Box>

        <div className="ag-theme-quartz" style={{ width: "100%", height: TABLE_HEIGHT }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            quickFilterText={searchText}
            overlayLoadingTemplate={`<span>${T.loading}</span>`}
            overlayNoRowsTemplate={`<span>${T.empty}</span>`}
            onCellClicked={onCellClicked}
          />
        </div>
      </PageContentBox>

      <DeleteEducationItemDialog
        open={dialogOpen}
        itemName={selected?.name ?? ""}
        isDeleting={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDialogOpen(false);
          setSelected(null);
        }}
      />

      <Snackbar
        open={!!successMsg}
        autoHideDuration={SNACKBAR_AUTO_HIDE_MS}
        onClose={() => setSuccessMsg("")}
      >
        <Alert severity="success" onClose={() => setSuccessMsg("")}>
          {successMsg}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!errorMsg}
        autoHideDuration={SNACKBAR_AUTO_HIDE_MS}
        onClose={() => setErrorMsg("")}
      >
        <Alert severity="error" onClose={() => setErrorMsg("")}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default EducationItemListPage;
